import {
  SimpleProductCustomizableDropDownOption,
  SimpleProductCustomizableDropDownValue,
  SimpleProductDetailsFragment,
} from '../../../graphql/magento'
import useUserCategory from '../../../hooks/useUserCategory'
import { useTestKitDelayedBilling } from './useTestKitDelayedBilling'

interface PayUpfrontOption {
  optionId: number
  price: number
  title: string
  valueString: string
}

// Returns customizable dropdown option for "Pay upfront" if the user is a practitioner
// and test kit delayed billing applies
export const usePayUpfrontOption = (
  product: SimpleProductDetailsFragment | null | undefined,
): PayUpfrontOption | undefined => {
  const { userCategory } = useUserCategory()
  const testKitDelayedBilling = useTestKitDelayedBilling(product)
  const isPractitioner = userCategory === 'practitioner'

  if (!isPractitioner || !testKitDelayedBilling) {
    return undefined
  }

  const eligibleDropdownOption = product?.options?.find(
    (option): option is SimpleProductCustomizableDropDownOption =>
      option?.__typename === 'CustomizableDropDownOption' &&
      Array.isArray(option.value) &&
      option.value.some((v) => v?.title?.trim().toLowerCase() === 'pay upfront'),
  )
  if (!eligibleDropdownOption) {
    return undefined
  }

  const optionValue = eligibleDropdownOption.value
    .filter((v): v is SimpleProductCustomizableDropDownValue => !!v?.__typename)
    .find(
      ({ title, price, price_type: priceType }) =>
        title.trim().toLowerCase() === 'pay upfront' && price > 0 && priceType === 'FIXED',
    )
  if (!optionValue) {
    return undefined
  }

  return {
    optionId: eligibleDropdownOption.option_id,
    price: optionValue.price,
    title: optionValue.title.trim(),
    valueString: String(optionValue.option_type_id),
  }
}
